import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Phone,
  FileText,
  Sparkles,
  Repeat,
  Star,
  CircleHelp,
  ArrowDown,
  ArrowDownWideNarrow,
  CheckSquare,
  Loader,
} from 'lucide-react';
import useOrderStore from '../../../core/stores/orderStore';
import useAuthStore from '../../../core/stores/authStore';
import useFavoritesStore from '../../../core/stores/favoritesStore';
import StatusTag from '../../../core/components/dashboard/StatusTag/StatusTag';
import {
  RX_STATUS_TO_TAG,
  HIDDEN_STATES_IN_RX,
  STATUS_TAG_DEFS,
} from '../../../core/constants/statusTags';
import { useUpdateOrderStatus } from '../../hooks/prescription/useUpdateOrderStatus';
import { usePrescriptionList } from '../../hooks/prescription/usePrescriptionList';
import { getFcmService } from '../../../domains/fcm';
import { ORDER_STATUS } from '../../../core/constants/orderStatus';
import LogUtil from '../../../core/utils/LogUtil';
import InputModal from '../../../core/components/dashboard/Common/InputModal';

/* 처방전 상태 → 태그 키 매핑은 core/constants/statusTags.js의 RX_STATUS_TO_TAG 사용
   처방전 관리 탭에서는 HIDDEN_STATES_IN_RX(visiting/pos)는 표시 안 함 */

const FILTER_TABS = [
  { id: 'all', label: '전체', statuses: null },
  { id: 'pending', label: '승인대기', statuses: [ORDER_STATUS.PENDING] },
  { id: 'paid', label: '결제완료', statuses: [ORDER_STATUS.PAID] },
  { id: 'preparing', label: '조제중', statuses: [ORDER_STATUS.PREPARING] },
  { id: 'completed', label: '조제완료', statuses: [ORDER_STATUS.COMPLETED] },
  { id: 'pickup', label: '수령완료', statuses: [ORDER_STATUS.PICKED_UP] },
  { id: 'rejected', label: '조제불가', statuses: [ORDER_STATUS.REJECTED] },
  { id: 'waiting', label: '대기요청', statuses: [ORDER_STATUS.WAITING] },
];

// /prescription/list items[].visit_channel — 주문은 현재 전부 'app'
const VISIT_CHANNEL_LABEL = { app: '앱으로 접수' };

const formatTime = iso => {
  if (!iso) return '-';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatDateLong = iso => {
  if (!iso) return '-';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

// Figma 15:6657 checkbox__item — 24 박스 안 18(inset 12.5%). 체크: #007aff 채움 + 흰 체크, 해제: 0.75 #c1c8cf 테두리, r3
const Checkbox = ({ checked }) => (
  <span className="relative block shrink-0" style={{ width: 24, height: 24 }}>
    {checked ? (
      <svg className="absolute" style={{ inset: 3 }} width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <rect width="18" height="18" rx="3" fill="#007AFF" />
        <path d="M7.16257 13.4997L2.88757 9.22471L3.95632 8.15596L7.16257 11.3622L14.0438 4.48096L15.1126 5.54971L7.16257 13.4997Z" fill="white" />
      </svg>
    ) : (
      <span className="absolute" style={{ inset: 3, border: '0.75px solid #c1c8cf', borderRadius: 3 }} />
    )}
  </span>
);

const isResultOk = res => res?.result_code === '200' || res?.resultCode === 200;

/* ─────────────────────────────────────────
   좌측 처방전 list 패널
   ───────────────────────────────────────── */

const PrescriptionList = ({
  orders,
  filter,
  setFilter,
  selectedId,
  onSelect,
  statusCounts,
  sort,
  onToggleSort,
}) => {
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // 백엔드 status_counts{all, <상태>…} (상태 필터 제외·날짜 필터 적용)
  const filterCounts = useMemo(() => {
    if (!statusCounts) return {};
    const counts = { all: statusCounts.all ?? 0 };
    FILTER_TABS.forEach(t => {
      if (!t.statuses) return;
      counts[t.id] = t.statuses.reduce((sum, st) => sum + (statusCounts[st] ?? 0), 0);
    });
    return counts;
  }, [statusCounts]);
  const totalCount = statusCounts?.all;

  const filtered = useMemo(() => {
    const tab = FILTER_TABS.find(t => t.id === filter);
    if (!tab || !tab.statuses) return orders;
    return orders.filter(o => tab.statuses.includes(o.status));
  }, [orders, filter]);

  const toggleSelectMode = () => {
    setSelectMode(v => !v);
    setSelectedIds(new Set());
  };

  const toggleRow = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () =>
    setSelectedIds(new Set(filtered.map(o => o.order_id)));
  const clearAll = () => setSelectedIds(new Set());

  return (
    <section
      className="flex min-w-0 flex-col border-r border-zinc-200 bg-white"
      style={{ flex: '0 0 60.16%' }}
    >
      <header className="px-6 pt-6">
        <div className="flex flex-wrap items-start gap-2">
          {FILTER_TABS.map(t => {
            const active = filter === t.id;
            const count = filterCounts[t.id];
            return (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 transition-colors"
                style={{
                  fontSize: 'var(--text-15)',
                  lineHeight: '22px',
                  letterSpacing: '-0.3px',
                  background: active ? '#e5f2ff' : '#f4f4f5',
                  color: active ? '#007aff' : '#71717a',
                }}
              >
                <span style={{ fontWeight: active ? 600 : 500 }}>
                  {t.label}
                </span>
                {count != null && (
                  <span style={{ fontWeight: 500 }}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
      </header>

      <div className="mx-6 mb-6 mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex h-9 items-center justify-between border-y border-zinc-200 bg-[#fafafa] px-2">
          <div className="flex items-center gap-1 px-2 text-14 tracking-[-0.28px] text-zinc-700">
            <span style={{ fontWeight: 500 }}>총</span>
            <span style={{ fontWeight: 600 }}>
              {totalCount != null ? `${totalCount.toLocaleString()}명` : '-'}
            </span>
          </div>
          {/* Figma 15:6649 header__actions — gap 24, px12 */}
          <div className="flex items-center justify-end gap-6 px-3">
            <button
              onClick={onToggleSort}
              className="flex items-center gap-1"
              style={{ color: '#71717a', fontSize: 'var(--text-12)', lineHeight: '18px' }}
            >
              <span>최신 방문 순</span>
              <ArrowDownWideNarrow size={16} strokeWidth={1.5} />
            </button>
            <button
              onClick={toggleSelectMode}
              className="flex items-center gap-1"
              style={{
                color: selectMode ? '#007aff' : '#71717a',
                fontWeight: selectMode ? 600 : 400,
                fontSize: 'var(--text-12)',
                lineHeight: '18px',
              }}
            >
              <CheckSquare size={16} strokeWidth={1.5} />
              <span>선택</span>
            </button>
          </div>
        </div>

        {selectMode && (
          // Figma 15:6650 — h36(py8), bg white, border-b. 좌 M14 px12 gap4 / 우 R12 underline px12 gap24
          <div
            className="flex h-9 items-center justify-between border-b border-zinc-200 bg-white whitespace-nowrap"
          >
            <div
              className="flex items-center gap-1 px-3"
              style={{ fontSize: 'var(--text-14)', fontWeight: 500, lineHeight: '20px', letterSpacing: '-0.28px' }}
            >
              <span style={{ color: '#3f3f46' }}>
                {selectedIds.size}
              </span>
              <span style={{ color: '#a1a1aa' }}>
                건 선택됨
              </span>
            </div>
            <div className="flex items-center gap-6 px-3">
              <button
                onClick={clearAll}
                style={{
                  fontSize: 'var(--text-12)',
                  lineHeight: '18px',
                  color: '#71717a',
                  textDecoration: 'underline',
                }}
              >
                취소
              </button>
              <button
                onClick={selectAll}
                style={{
                  fontSize: 'var(--text-12)',
                  lineHeight: '18px',
                  color: '#71717a',
                  textDecoration: 'underline',
                }}
              >
                전체 선택
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-(length:--text-14) text-zinc-400">
              처방전이 없습니다
            </div>
          ) : (
            <ul className="flex flex-col">
              {filtered.map(o => {
                const tagState = RX_STATUS_TO_TAG[o.status] || 'pending';
                const selected = selectedId === o.order_id;
                const checked = selectedIds.has(o.order_id);
                const tag =
                  STATUS_TAG_DEFS[tagState] || STATUS_TAG_DEFS.pending;
                const meds = (o.medications || [])
                  .map(m => m.medication_name || m.name)
                  .filter(Boolean);
                const medsSummary =
                  meds.length > 0
                    ? meds.length === 1
                      ? meds[0]
                      : `${meds[0]} 외 ${meds.length - 1}종`
                    : '';
                const genderText =
                  o.user?.gender === 'M'
                    ? '남'
                    : o.user?.gender === 'F'
                      ? '여'
                      : '';
                const ageText = o.user?.age != null ? `${o.user.age}세` : '';
                return (
                  <li
                    key={o.order_id}
                    onClick={() =>
                      selectMode ? toggleRow(o.order_id) : onSelect(o.order_id)
                    }
                    className="flex h-[60px] cursor-pointer items-center border-b border-zinc-200 transition-colors"
                    style={{
                      background:
                        (selectMode ? checked : selected) ? '#f3f9ff' : 'transparent',
                    }}
                  >
                    {selectMode && (
                      <button
                        className="flex shrink-0 py-2.5 pl-3"
                        aria-label={checked ? '선택 해제' : '선택'}
                      >
                        <Checkbox checked={checked} />
                      </button>
                    )}
                    <div className="flex h-full min-w-[70px] max-w-[100px] flex-1 items-center justify-center px-3">
                      <span
                        className="whitespace-nowrap text-14 font-semibold tracking-[-0.28px]"
                        style={{ color: tag.color }}
                      >
                        {tag.label}
                      </span>
                    </div>
                    <div className="flex h-full min-w-[70px] max-w-[100px] flex-1 items-center px-3 text-16 font-medium tracking-[-0.32px] text-zinc-700">
                      <span className="truncate">{o.user?.name || '-'}</span>
                    </div>
                    <div className="flex h-full min-w-[70px] max-w-[90px] flex-1 items-center px-3 text-14 tracking-[-0.28px] text-zinc-700">
                      <span className="truncate">
                        {[genderText, ageText].filter(Boolean).join(' · ') ||
                          '-'}
                      </span>
                    </div>
                    <div className="flex min-w-[160px] flex-1 flex-col justify-center gap-0.5 px-3 text-14 tracking-[-0.28px]">
                      <span className="truncate font-medium text-zinc-700">
                        {o.hospital_name || '-'}
                      </span>
                      <span className="truncate text-zinc-500">
                        {[o.department, medsSummary]
                          .filter(Boolean)
                          .join(' · ') || '-'}
                      </span>
                    </div>
                    <div className="flex h-full min-w-[72px] shrink-0 items-center justify-end whitespace-nowrap px-3 text-right text-14 tabular-nums text-zinc-500">
                      {formatTime(o.created_at)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   우측 처방 상세 패널
   ───────────────────────────────────────── */

const PrescriptionDetail = ({
  order,
  aiInsight,
  substitution,
  onAction,
  isCalling,
  onCall,
  onShowImage,
  isFavorite,
  onToggleFavorite,
}) => {
  if (!order) {
    return (
      <section
        className="flex items-center justify-center bg-[#fafafa]"
        style={{ flex: '0 0 39.84%' }}
      >
        <p className="text-14 text-zinc-400">왼쪽에서 처방전을 선택하세요</p>
      </section>
    );
  }

  const tagState = RX_STATUS_TO_TAG[order.status] || 'pending';
  const meds = order.medications || [];
  const consentStatusLabel = {
    pending: '환자 동의 대기',
    agreed: '환자 동의 완료',
    declined: '환자 거부',
  };

  return (
    <section
      className="flex min-w-0 flex-col bg-[#fafafa]"
      style={{ flex: '0 0 39.84%' }}
    >
      <div className="px-6 pt-6">
        {/* Figma 15:6527 patient — gap 4, pb 6 */}
        <div className="flex min-h-[57px] flex-col gap-1 pb-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-1">
              <span className="truncate text-20 font-semibold leading-[27px] tracking-[-0.4px] text-zinc-900">
                {order.user?.name || '-'}
              </span>
              <button
                onClick={e => {
                  e.stopPropagation();
                  onToggleFavorite?.();
                }}
                className="flex shrink-0 items-center transition-opacity hover:opacity-70"
                aria-label={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
              >
                <Star
                  size={18}
                  className={
                    isFavorite
                      ? 'fill-amber-400 text-amber-400'
                      : 'text-zinc-300'
                  }
                  strokeWidth={1.5}
                />
              </button>
              <StatusTag state={tagState} hide={HIDDEN_STATES_IN_RX} />
            </div>
            <span className="shrink-0 text-14 font-medium tracking-[-0.28px] text-zinc-500">
              {[VISIT_CHANNEL_LABEL[order.visit_channel], formatTime(order.created_at)]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          <div className="flex items-start justify-between gap-3 text-14 font-medium tracking-[-0.28px] text-zinc-500">
            {/* 남 · 72세 — M14 #52525b, 점 #bcbcc2, gap 2 */}
            <span className="flex gap-0.5 text-[#52525b]">
              {[
                order.user?.gender === 'M'
                  ? '남'
                  : order.user?.gender === 'F'
                    ? '여'
                    : '',
                order.user?.age != null ? `${order.user.age}세` : '',
              ]
                .filter(Boolean)
                .flatMap((v, i) => (i === 0 ? [v] : ['·', v]))
                .map((v, i) => (
                  <span key={i} className={v === '·' ? 'text-[#bcbcc2]' : undefined}>{v}</span>
                ))}
              {order.user?.gender == null && order.user?.age == null && '-'}
            </span>
            {/* 포인트·쿠폰은 백엔드가 null (정산 서버 원장) — null 이면 숨김 */}
            <div className="flex items-center gap-3 whitespace-nowrap">
              {[
                order.user?.visit_count != null &&
                  `내원 ${order.user.visit_count}회`,
                order.user?.points != null &&
                  `${order.user.points.toLocaleString()}P`,
                order.user?.coupons?.available != null &&
                  `쿠폰 ${order.user.coupons.available}장`,
              ]
                .filter(Boolean)
                .flatMap((v, i) => [
                  i > 0 && (
                    <span key={`d${i}`} className="text-zinc-200">|</span>
                  ),
                  <span key={i}>{v}</span>,
                ])}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h3 className="mb-3 text-16 font-semibold tracking-[-0.32px] text-[#27272a]">
            {[order.hospital_name, order.department]
              .filter(Boolean)
              .join(' · ') || '-'}
          </h3>
          {/* Figma 15:6528 Row — flex-wrap, row: flex-1 min-w220 h28 border-y / Label w100 bg #f4f4f5 px8 / Data px8 */}
          <div className="flex w-full flex-wrap text-14 tracking-[-0.28px]">
            {[
              {
                label: '진단명',
                value: `${order.diagnosis || '-'}${order.disease_code ? `(${order.disease_code})` : ''}`,
              },
              {
                label: '처방일',
                value: formatDateLong(order.prescription_date || order.created_at),
              },
            ].map(it => (
              <div
                key={it.label}
                className="flex h-7 min-w-[220px] flex-1 items-center border-y border-zinc-200 bg-white"
              >
                <span className="flex h-full w-[100px] shrink-0 items-center truncate bg-zinc-100 px-2 text-zinc-500">
                  {it.label}
                </span>
                <span className="min-w-0 flex-1 truncate px-2 text-zinc-700">
                  {it.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto px-6 pb-8 pt-8">
        {/* AI 인사이트 — Figma 396:5888 */}
        {aiInsight?.insight_text && (
          <div className="flex w-full flex-col gap-1">
            {/* Figma 15:6530 — 제목 뒤 아이콘 18, gap 4 */}
            <div className="flex h-[22px] items-center gap-1">
              <p className="whitespace-nowrap text-16 font-semibold tracking-[-0.32px] text-[#1d356d]">
                AI 인사이트
              </p>
              <Sparkles
                size={18}
                className="shrink-0 text-[#1d356d]"
                strokeWidth={1.5}
              />
            </div>
            <p className="whitespace-pre-wrap text-14-long tracking-[-0.28px] text-[#3f3f46]">
              {aiInsight.insight_text}
            </p>
          </div>
        )}

        {/* 대체조제 진행 — Figma 15:6535 (카드 흰색 p16 r10 gap12, 처방/대체 행 #fafafa + border-y) */}
        {substitution && (
          <div className="flex w-full flex-col gap-3 overflow-hidden rounded-[10px] bg-white p-4">
            <div className="flex h-[22px] items-center gap-1.5">
              <Repeat size={16} className="shrink-0 text-[#f79103]" strokeWidth={1.5} />
              <p className="flex-1 text-16 font-semibold tracking-[-0.32px] text-[#f79103]">
                대체조제 진행
              </p>
              {/* tag/square — h22 px6 py2 r8 gap4, M14 #71717a */}
              <span className="inline-flex h-[22px] shrink-0 items-center justify-center gap-1 rounded-lg px-1.5 py-0.5">
                <Loader
                  size={16}
                  className="text-[#71717a]"
                  strokeWidth={1.5}
                />
                <span className="whitespace-nowrap text-14 font-medium tracking-[-0.28px] text-[#71717a]">
                  {consentStatusLabel[substitution.patient_consent_status] ||
                    '환자 동의 대기'}
                </span>
              </span>
            </div>

            <div className="flex w-full flex-col items-center text-14 tracking-[-0.28px]">
              {/* table — 처방/화살표/대체 gap 6 */}
              <div className="flex w-full flex-col items-center gap-1.5">
                <div className="flex w-full items-center gap-3 border-y border-zinc-200 bg-[#fafafa] px-3 py-1.5">
                  <span className="w-24 shrink-0 font-medium leading-5 text-[#71717a]">
                    처방 의약품
                  </span>
                  <span className="whitespace-nowrap leading-[22px] text-[#18181b]">
                    {substitution.original_drug}
                  </span>
                </div>
                <ArrowDown
                  size={18}
                  className="shrink-0 text-zinc-500"
                  strokeWidth={1.5}
                />
                <div className="flex w-full items-center gap-3 border-y border-zinc-200 bg-[#fafafa] px-3 py-1.5">
                  <span className="w-24 shrink-0 font-medium leading-5 text-[#71717a]">
                    대체 조제 의약품
                  </span>
                  <span className="whitespace-nowrap font-semibold leading-5 text-[#f79103]">
                    {substitution.substitute_drug}
                  </span>
                </div>
              </div>
              <div className="flex w-full items-center gap-3 border-b border-zinc-200 bg-white px-3 py-1.5 font-medium">
                <span className="w-24 shrink-0 leading-5 text-[#71717a]">
                  약값 차이
                </span>
                <span className="whitespace-nowrap tabular-nums leading-5 text-[#18181b]">
                  {substitution.price_diff > 0 ? '+' : ''}
                  {substitution.price_diff}원
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 처방의약품 섹션 — Figma 394:4532 gap-12 */}
        <div className="flex flex-col gap-3 w-full">
          <div
            className="flex items-center gap-2.5 h-5 w-full"
            style={{
              fontSize: 'var(--text-16)',
              fontWeight: 600,
              color: '#18181b',
              lineHeight: '22px',
              letterSpacing: '-0.32px',
            }}
          >
            <span>처방의약품</span>
            <span>{meds.length}</span>
          </div>
          <div className="w-full overflow-hidden border-y border-zinc-200">
            <table className="w-full">
              <thead>
                {/* Figma 15:6557 header row — h44, bg #f4f4f5, M14 */}
                <tr
                  style={{
                    height: 44,
                    background: '#f4f4f5',
                    borderBottom: '1px solid #e4e4e7',
                  }}
                >
                  <th
                    className="text-left"
                    style={{
                      padding: '0 12px',
                      fontSize: 'var(--text-14)',
                      fontWeight: 500,
                      color: '#71717a',
                      lineHeight: '20px',
                      letterSpacing: '-0.28px',
                    }}
                  >
                    <div>약품명</div>
                  </th>
                  <th
                    className="text-center whitespace-nowrap"
                    style={{
                      padding: '0 12px',
                      width: 80,
                      fontSize: 'var(--text-14)',
                      fontWeight: 500,
                      color: '#3f3f46',
                      lineHeight: '20px',
                      letterSpacing: '-0.28px',
                    }}
                  >
                    1회 투약량
                  </th>
                  <th
                    className="text-center whitespace-nowrap"
                    style={{
                      padding: '0 12px',
                      width: 80,
                      fontSize: 'var(--text-14)',
                      fontWeight: 500,
                      color: '#3f3f46',
                      lineHeight: '20px',
                      letterSpacing: '-0.28px',
                    }}
                  >
                    1일 투여횟수
                  </th>
                  <th
                    className="text-center whitespace-nowrap"
                    style={{
                      padding: '0 12px',
                      width: 80,
                      fontSize: 'var(--text-14)',
                      fontWeight: 500,
                      color: '#3f3f46',
                      lineHeight: '20px',
                      letterSpacing: '-0.28px',
                    }}
                  >
                    총 투약일수
                  </th>
                </tr>
              </thead>
              <tbody>
                {meds.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center py-8"
                      style={{ fontSize: 'var(--text-14)', color: '#a1a1aa' }}
                    >
                      처방 의약품 정보가 없습니다
                    </td>
                  </tr>
                ) : (
                  meds.map((m, idx) => (
                    <tr
                      key={idx}
                      className="h-[60px] border-b border-zinc-200 last:border-b-0"
                    >
                      <td className="px-3 py-1.5">
                        <div className="flex flex-col gap-0.5 text-14 tracking-[-0.28px] text-zinc-700">
                          <div className="font-medium text-zinc-900">
                            {m.medication_name || m.name || '-'}
                          </div>
                          {m.ingredient && (
                            <div className="text-12 ">
                              ({m.ingredient})
                            </div>
                          )}
                        </div>
                      </td>
                      <td
                        className="text-center tabular-nums"
                        style={{
                          padding: '0 12px',
                          fontSize: 'var(--text-14)',
                          color: '#3f3f46',
                          lineHeight: '20px',
                          letterSpacing: '-0.28px',
                        }}
                      >
                        {m.dosage_per_dose ?? '-'}
                      </td>
                      <td
                        className="text-center tabular-nums"
                        style={{
                          padding: '0 12px',
                          fontSize: 'var(--text-14)',
                          color: '#3f3f46',
                          lineHeight: '20px',
                          letterSpacing: '-0.28px',
                        }}
                      >
                        {m.frequency_per_day ?? '-'}
                      </td>
                      <td
                        className="text-center tabular-nums"
                        style={{
                          padding: '0 12px',
                          fontSize: 'var(--text-14)',
                          color: '#3f3f46',
                          lineHeight: '20px',
                          letterSpacing: '-0.28px',
                        }}
                      >
                        {m.total_days ?? '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {(order.qr_info?.qr_code || order.total_amount != null) && (
          <div className="flex w-full items-start justify-between">
            {/* QR Container — Figma 15:6562 gap-12 */}
            {order.qr_info?.qr_code && (
              <div className="flex items-start gap-3 self-stretch">
                <div
                  className="bg-white flex items-center"
                  style={{
                    border: '1px solid #e4e4e7',
                    borderRadius: '5.333px',
                    padding: 6,
                  }}
                >
                  <div style={{ width: 68, height: 68 }}>
                    <QRCodeSVG
                      value={order.qr_info.qr_code}
                      size={68}
                      level="M"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1 items-start whitespace-nowrap">
                  <span
                    style={{
                      fontSize: 'var(--text-12)',
                      fontWeight: 500,
                      color: '#3f3f46',
                      lineHeight: '18px',
                    }}
                  >
                    처방전 QR
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--text-12)',
                      color: '#71717a',
                      lineHeight: '18px',
                    }}
                    className="tabular-nums"
                  >
                    {order.qr_info.qr_code_id || order.order_id}
                  </span>
                </div>
              </div>
            )}

            {/* 약제비 다이얼로그 — Figma 15:6636 (gap 12, px20 py16, r8) */}
            {order.total_amount != null && (
              <div
                className="flex w-[200px] items-center gap-3 whitespace-nowrap rounded-lg bg-white px-5 py-4"
                style={{
                  minWidth: 200,
                }}
              >
                <span
                  style={{ fontSize: 'var(--text-12)', fontWeight: 500, color: '#3f3f46', lineHeight: '18px' }}
                >
                  약제비
                </span>
                <span
                  style={{
                    fontSize: 'var(--text-20)',
                    fontWeight: 600,
                    color: '#18181b',
                    lineHeight: 1.35,
                    letterSpacing: '-0.4px',
                  }}
                  className="min-w-0 flex-1 text-right tabular-nums"
                >
                  ₩ {order.total_amount.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 액션 버튼 풋터 — Figma 396:5789 */}
      <footer
        className="flex shrink-0 items-center justify-end gap-2 border-t border-zinc-200 bg-white"
        style={{
          padding: 12,
          filter: 'drop-shadow(0px -5px 4.1px rgba(11,11,11,0.04))',
        }}
      >
        <button
          onClick={() => onCall(order.user?.user_id || order.user?.id)}
          disabled={isCalling}
          className="flex items-center justify-center gap-1 whitespace-nowrap disabled:opacity-50"
          style={{
            height: 44,
            background: '#f4f4f5',
            borderRadius: 6,
            padding: '14px 10px',
          }}
        >
          <Phone size={16} className="text-[#3f3f46]" strokeWidth={1.5} />
          <span
            style={{
              fontSize: 'var(--text-16)',
              fontWeight: 600,
              color: '#3f3f46',
              letterSpacing: '-0.32px',
              lineHeight: '22px',
            }}
          >
            전화
          </span>
        </button>

        <button
          onClick={() => onShowImage(order.prescription_image_url)}
          disabled={!order.prescription_image_url}
          className="flex items-center justify-center gap-1 disabled:opacity-50 whitespace-nowrap"
          style={{
            height: 44,
            background: '#f4f4f5',
            borderRadius: 6,
            padding: '14px 10px',
          }}
        >
          <FileText size={16} className="text-[#3f3f46]" strokeWidth={1.5} />
          <span
            style={{
              fontSize: 'var(--text-16)',
              fontWeight: 600,
              color: '#3f3f46',
              letterSpacing: '-0.32px',
              lineHeight: '22px',
            }}
          >
            원본
          </span>
        </button>

        {/* PENDING 상태: 조제 불가 (빨강) + 승인하고 조제 시작 (Cresoty blue) */}
        {order.status === ORDER_STATUS.PENDING && (
          <>
            <button
              onClick={() => onAction(order.order_id, 'reject')}
              className="flex items-center justify-center"
              style={{
                height: 44,
                background: '#fdecee',
                borderRadius: 6,
                padding: '14px 10px',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--text-16)',
                  fontWeight: 600,
                  color: '#ff3b30',
                  letterSpacing: '-0.32px',
                  lineHeight: '22px',
                }}
              >
                조제불가
              </span>
            </button>
            <button
              type="button"
              onClick={() => onAction(order.order_id, 'waiting')}
              className="flex h-11 items-center justify-center gap-1 whitespace-nowrap border border-zinc-200 bg-white px-2.5"
              style={{ borderRadius: 6 }}
            >
              <span className="text-16 font-semibold tracking-[-0.32px] text-zinc-700">
                대기요청
              </span>
              <CircleHelp
                size={16}
                className="text-zinc-300"
                strokeWidth={1.5}
              />
            </button>
            <button
              onClick={() => onAction(order.order_id, 'approve')}
              className="flex flex-1 items-center justify-center min-w-0"
              style={{
                height: 44,
                background: '#1d356d',
                borderRadius: 6,
                padding: '14px 24px',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--text-16)',
                  fontWeight: 600,
                  color: '#fff',
                  letterSpacing: '-0.32px',
                  lineHeight: '22px',
                }}
              >
                승인하고 조제 시작
              </span>
            </button>
          </>
        )}

        {/* PAID 상태: 조제 시작 */}
        {order.status === ORDER_STATUS.PAID && (
          <button
            onClick={() => onAction(order.order_id, 'preparing')}
            className="flex flex-1 items-center justify-center min-w-0"
            style={{
              height: 44,
              background: '#1d356d',
              borderRadius: 6,
              padding: '14px 24px',
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-16)',
                fontWeight: 600,
                color: '#fff',
                letterSpacing: '-0.32px',
                lineHeight: '22px',
              }}
            >
              조제 시작
            </span>
          </button>
        )}

        {/* PREPARING 상태: 조제 완료 */}
        {order.status === ORDER_STATUS.PREPARING && (
          <button
            onClick={() => onAction(order.order_id, 'completed')}
            className="flex flex-1 items-center justify-center min-w-0"
            style={{
              height: 44,
              background: '#1d356d',
              borderRadius: 6,
              padding: '14px 24px',
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-16)',
                fontWeight: 600,
                color: '#fff',
                letterSpacing: '-0.32px',
                lineHeight: '22px',
              }}
            >
              조제 완료
            </span>
          </button>
        )}
      </footer>
    </section>
  );
};

/* ─────────────────────────────────────────
   RxPage
   ───────────────────────────────────────── */

const RxPage = () => {
  const location = useLocation();
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [isCalling, setIsCalling] = useState(false);
  const [inputModal, setInputModal] = useState(null);
  const [imageModal, setImageModal] = useState(null);

  // 즐겨찾는 환자 store (로그인 시 fetch됨, user_id 기준)
  const pharmacyBrn = useAuthStore(
    s => s.pharmacy?.business_registration_number
  );
  const favoriteUserIds = useFavoritesStore(s => s.userIds);
  const toggleFavoriteAction = useFavoritesStore(s => s.toggle);

  // user_id 기준 즐겨찾기 여부 (favoritesStore가 단일 진실)
  const isFavorite = order => {
    if (!order) return false;
    const uid = order.user?.user_id ?? order.user?.id;
    return uid != null && favoriteUserIds.includes(uid);
  };

  const toggleFavorite = order => {
    if (!order || !pharmacyBrn) return;
    const uid = order.user?.user_id ?? order.user?.id;
    if (!uid) return;
    toggleFavoriteAction(pharmacyBrn, uid);
  };

  // MainLayout 폴링 store 구독 (접수순 목록 실시간 갱신)
  const storeOrders = useOrderStore(s => s.orders);
  const lastUpdate = useOrderStore(s => s.lastUpdate);
  // 탭 개수(status_counts) + '최신 방문 순' 목록 — 폴링이 변경을 감지(lastUpdate)할 때 재조회
  const [sort, setSort] = useState('received_at');
  const listQuery = usePrescriptionList(sort, lastUpdate?.getTime?.());
  const orders =
    sort === 'latest_visit' && listQuery.data ? listQuery.data.items : storeOrders;

  // 포인트·쿠폰(정산 서버 조회)은 폴링 목록엔 없고 이 화면 목록(include_benefits)에만 온다
  const selectedOrder = useMemo(() => {
    const base = orders.find(o => o.order_id === selectedId);
    if (!base) return null;
    const benefits = listQuery.data?.items.find(o => o.order_id === selectedId)?.user;
    if (!benefits) return base;
    return {
      ...base,
      user: { ...base.user, points: benefits.points, coupons: benefits.coupons },
    };
  }, [orders, selectedId, listQuery.data]);

  // 모달에서 "주문 상세 보기" 클릭 시 자동 선택
  useEffect(() => {
    const targetId = location.state?.selectedOrderId;
    if (targetId && orders.length > 0) {
      const found = orders.find(o => o.order_id === targetId);
      if (found) setSelectedId(targetId);
    }
  }, [location.state, orders]);

  // 첫 주문 자동 선택 (선택 없을 때)
  useEffect(() => {
    if (!selectedId && orders.length > 0) {
      setSelectedId(orders[0].order_id);
    }
  }, [selectedId, orders]);

  // AI 인사이트·대체조제 숨김 (10/22 결정): 저장소가 모든 처방전에 같은 고정 문장·약을 돌려줌.
  // 대체조제는 PMS·약가 데이터가 없어 불가, AI 인사이트는 문구 규칙 확정 후 API 연결 시 다시 켠다.

  /* 액션 핸들러 */
  const refreshOrders = () => window.dispatchEvent(new Event('refreshOrders'));
  const updateStatusMutation = useUpdateOrderStatus();

  const executeStatusChange = async (orderId, newStatus, additional = {}) => {
    try {
      await updateStatusMutation.mutateAsync({
        orderId,
        status: newStatus,
        additional,
      });
      refreshOrders();
    } catch (err) {
      LogUtil.error('[RxPage] 상태 변경 실패:', err);
      alert('상태 변경에 실패했습니다.');
    }
  };

  const handleAction = (orderId, action) => {
    if (action === 'waiting') {
      executeStatusChange(orderId, ORDER_STATUS.WAITING);
      return;
    }
    if (action === 'approve') {
      setInputModal({
        title: '결제 금액 입력',
        label: '결제 금액',
        placeholder: '금액을 입력하세요',
        type: 'number',
        confirmText: '승인',
        confirmColor: '#18181B',
        onConfirm: value => {
          setInputModal(null);
          executeStatusChange(orderId, ORDER_STATUS.APPROVED, {
            total_amount: Number(value),
          });
        },
      });
      return;
    }
    if (action === 'reject') {
      setInputModal({
        title: '조제 불가 사유',
        label: '거절 사유',
        placeholder: '사유를 입력하세요',
        type: 'text',
        confirmText: '확인',
        confirmColor: '#EF4444',
        onConfirm: value => {
          setInputModal(null);
          executeStatusChange(orderId, ORDER_STATUS.REJECTED, {
            rejection_reason: value,
          });
        },
      });
      return;
    }
    if (action === 'preparing') {
      executeStatusChange(orderId, ORDER_STATUS.PREPARING);
      return;
    }
    if (action === 'completed') {
      executeStatusChange(orderId, ORDER_STATUS.COMPLETED);
      return;
    }
  };

  const handleCall = async userId => {
    if (!userId) {
      alert('사용자 정보가 없습니다.');
      return;
    }
    try {
      setIsCalling(true);
      const res = await getFcmService().sendVoipCall(userId);
      if (isResultOk(res)) {
        alert('전화 발신 성공');
      } else {
        alert(res?.message || '전화 발신에 실패했습니다.');
      }
    } catch (err) {
      LogUtil.error('[RxPage] VoIP 전화 발신 실패:', err);
      alert('전화 발신에 실패했습니다.');
    } finally {
      setIsCalling(false);
    }
  };

  return (
    <>
      {/* Figma 394:2983 — 처방전 관리는 카드/padding 없이 화면 전체 사용, 좌패널 border-r로 분리 */}
      <div className="flex h-full overflow-hidden bg-white">
        <PrescriptionList
          orders={orders}
          filter={filter}
          setFilter={setFilter}
          selectedId={selectedId}
          onSelect={setSelectedId}
          statusCounts={listQuery.data?.statusCounts ?? null}
          sort={sort}
          onToggleSort={() =>
            setSort(s => (s === 'latest_visit' ? 'received_at' : 'latest_visit'))
          }
        />
        <PrescriptionDetail
          order={selectedOrder}
          onAction={handleAction}
          isCalling={isCalling}
          onCall={handleCall}
          onShowImage={setImageModal}
          isFavorite={isFavorite(selectedOrder)}
          onToggleFavorite={() => toggleFavorite(selectedOrder)}
        />
      </div>

      {inputModal && (
        <InputModal {...inputModal} onClose={() => setInputModal(null)} />
      )}
      {imageModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-8"
          onClick={() => setImageModal(null)}
        >
          <img
            src={imageModal}
            alt="처방전 원본"
            className="max-w-full max-h-full rounded-lg"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default RxPage;
